import { ReactElement } from 'react';

import { BackrsIcons } from '@backrs_ui/common-ui/src/atoms/BackrsIcon';

import { NuggetArticle } from '../NuggetArticle';
import { NuggetAudio } from '../NuggetAudio';
import { NuggetDocument } from '../NuggetDocument';
import { NuggetEvent } from '../NuggetEvent';
import { NuggetGeneratedContent } from '../NuggetGeneratedContent';
import { NuggetInstitution } from '../NuggetInstitution';
import { NuggetInternship } from '../NuggetInternship/NuggetInternship';
import { NuggetOpportunity } from '../NuggetOpportunity';
import { NuggetPost } from '../NuggetPost';
import { NuggetProfile } from '../NuggetProfile/NuggetProfile';
import { NuggetResource } from '../NuggetResource';
import { NuggetScholarship } from '../NuggetScholarship/NuggetScholarship';
import { NuggetTextResponse } from '../NuggetTextResponse';
import { NuggetUXVariant } from '../NuggetUITypes';
import { NuggetVideo } from '../NuggetVideo';

export function largeNuggetVariantMapper(variant: NuggetUXVariant): {
  component: ReactElement | null;
  icon: BackrsIcons;
} {
  switch (variant.type) {
    case 'resource':
      return {
        component: (
          <NuggetResource
            variant={{
              type: 'LARGE',
              title: variant.title,
              url: variant.url,
              previewImage: variant.previewImage,
            }}
          />
        ),
        icon: 'IconArticle',
      };

    case 'opportunity':
      return {
        component: (
          <NuggetOpportunity
            variant={{ type: 'LARGE', deadline: variant.deadline, mediaUrl: variant.mediaUrl }}
          />
        ),
        icon: 'IconOpportunity',
      };

    case 'event':
      return {
        component: <NuggetEvent variant={{ type: 'SMALL', date: variant.date }} />,
        icon: 'IconEvent',
      };

    case 'video-external':
      return {
        component: <NuggetVideo variant={{ type: 'LARGE', url: variant.url }} />,
        icon: 'IconVideo',
      };

    case 'video-internal':
      return {
        component: <NuggetVideo variant={{ type: 'LARGE', url: variant.url }} />,
        icon: 'IconVideo',
      };

    case 'article':
      return {
        component: (
          <NuggetArticle
            variant={{
              type: 'LARGE',
              articleUrl: variant.articleUrl,
              title: variant.title,
              previewImage: variant.previewImage,
            }}
          />
        ),
        icon: 'IconArticle',
      };

    case 'institution':
      return {
        component: (
          <NuggetInstitution
            variant={{
              type: 'LARGE',
              institutionUrl: variant.collegeInfo?.url || variant.url || '',
              previewImage: variant.previewImage,
            }}
          />
        ),
        icon: 'IconInstitution',
      };

    case 'employer':
      return {
        component: null,
        icon: 'IconInstitution',
      };

    case 'document':
      return {
        component: (
          <NuggetDocument
            variant={{
              type: 'LARGE',
              title: variant.title,
              internalMediaItemUrl: variant.internalMediaItemUrl,
              externalUrl: variant.externalUrl,
            }}
          />
        ),
        icon: 'IconDocument',
      };

    case 'audio':
      return {
        component: (
          <NuggetAudio
            variant={{
              type: 'LARGE',
              url: variant.url,
              title: variant.title,
              previewImage: variant.previewImage,
            }}
          />
        ),
        icon: 'IconAudio',
      };

    case 'text-response':
      return {
        component: (
          <NuggetTextResponse
            variant={{
              type: 'LARGE',
              text: variant.text,
              headshotImg: variant.headshotImg,
              linkToBackrsProfile: variant.linkToBackrsProfile,
              userAbbreviatedName: variant.userAbbreviatedName,
              postDate: variant.postDate,
              postUrl: variant.postUrl,
            }}
          />
        ),
        icon: 'IconTextResponse',
      };

    case 'post':
      return {
        component: (
          <NuggetPost
            variant={{
              type: 'LARGE',
              text: variant.text,
              headshotImg: variant.headshotImg,
              linkToBackrsProfile: variant.linkToBackrsProfile,
              userAbbreviatedName: variant.userAbbreviatedName,
              postDate: variant.postDate,
              postUrl: variant.postUrl,
            }}
          />
        ),
        icon: 'IconTextResponse',
      };

    case 'book':
      return {
        component: (
          <NuggetArticle
            variant={{
              type: 'LARGE',
              title: variant.title,
              articleUrl: variant.url || '',
              previewImage: variant.previewImage,
            }}
          />
        ),
        icon: 'IconBook',
      };

    case 'competency':
      return {
        component: null,
        icon: 'ProsperLogo',
      };

    case 'scholarships':
      return {
        component: (
          <NuggetScholarship
            variant={{ type: 'LARGE', scholarshipInfo: variant.scholarshipInfo }}
          />
        ),
        icon: 'IconScholarship',
      };

    case 'internship':
      return {
        component: (
          <NuggetInternship
            variant={{
              type: 'LARGE',
              internshipInfo: variant.internshipInfo,
            }}
          />
        ),
        icon: 'IconBriefcase',
      };

    case 'profile-external':
      return {
        component: (
          <NuggetProfile
            contentSize="LARGE"
            abbreviatedName={variant.abbreviatedName}
            location={variant.location}
            jobTitle={variant.jobTitle}
            employer={variant.employer}
            school={variant.school}
            profileImageUrl={variant.profileImageUrl}
            profileType="EXTERNAL"
          />
        ),
        icon: 'IconProfileExternal',
      };

    case 'profile-internal':
      return {
        component: (
          <NuggetProfile
            contentSize="LARGE"
            abbreviatedName={variant.abbreviatedName}
            location={variant.location}
            jobTitle={variant.jobTitle}
            employer={variant.employer}
            school={variant.school}
            role={variant.role}
            profileImageUrl={variant.profileImageUrl}
            profileType="INTERNAL"
          />
        ),
        icon: 'IconProfileInternal',
      };
    case 'generated-content':
      return {
        component: variant.generatedContentInfo ? (
          <NuggetGeneratedContent variant={variant.generatedContentInfo} />
        ) : (
          <></>
        ),
        icon: 'IconTextResponse',
      };

    case 'track-assessment-result':
    case 'track-assessment-unlock':
    case 'educational-investment':
    case 'career-track':
    case 'career-path':
    case 'tool':
    case 'major':
      return {
        component: null,
        icon: 'IconTextResponse',
      };

    case 'industry':
      return {
        component: null,
        icon: 'IconBuilding',
      };

    case 'coach':
      return {
        component: null,
        icon: 'IconBook',
      };

    case 'deep-dive':
      return {
        component: null,
        icon: 'IconBook',
      };
  }
}
